/**
 * Shared utilities for risk score evidence formatting and processing.
 * Used by both EnrichedAIResponse.tsx and EnrichedAIResponseV3.tsx.
 */

import { RiskScoreEvidence } from 'Generated/schema';
import { RiskClassification } from './RiskScoreScale/riskScoreScaleUtils';
import { RISK_SCORE_BLOCK_END } from './CollapsibleBlockV3';

// Data extracted from the Risk Scoring section for visualization
export interface RiskScoreData {
  score: number;
  classification: RiskClassification;
  benignCount: number;
  riskyCount: number;
  benignThreshold: number;
  riskyThreshold: number;
}

export const VALID_CLASSIFICATIONS: RiskClassification[] = ['Benign', 'Risky', 'Inconclusive'];

/**
 * Converts RiskScoreEvidence from the GraphQL API to RiskScoreData for visualization.
 */
export const convertRiskScoreEvidenceToData = (
  evidence: RiskScoreEvidence | null | undefined,
): RiskScoreData | null => {
  if (!evidence) {
    return null;
  }

  const classification = VALID_CLASSIFICATIONS.includes(
    evidence.classification as RiskClassification,
  )
    ? (evidence.classification as RiskClassification)
    : 'Inconclusive';

  return {
    score: evidence.normalizedTotalRiskScore,
    classification,
    benignCount: evidence.benignIndicators?.length ?? 0,
    riskyCount: evidence.riskyIndicators?.length ?? 0,
    benignThreshold: evidence.benignThreshold,
    riskyThreshold: evidence.riskyThreshold,
  };
};

// Formats RiskScoreEvidence into markdown for the collapsible Evidence block
export const formatRiskScoreEvidence = (evidence: RiskScoreEvidence | null | undefined): string => {
  if (!evidence) {
    return '';
  }

  const lines: string[] = [];

  const benignCount = evidence.benignIndicators?.length ?? 0;
  const riskyCount = evidence.riskyIndicators?.length ?? 0;

  lines.push(`**Scoring ${benignCount} benign indicators and ${riskyCount} risky indicators:**`);
  lines.push('');
  lines.push('| Type | Description | Score | Confidence | Context | Temporal |');
  lines.push('| --- | --- | --- | --- | --- | --- |');

  evidence.benignIndicators?.forEach(indicator => {
    lines.push(
      `| \`classification:Benign\` | ${indicator.description} | ${
        indicator.riskScore
      } | ${indicator.evidenceConfidence.toFixed(2)} | ${indicator.contextWeighting.toFixed(
        2,
      )} | ${indicator.temporalRelevance.toFixed(2)} |`,
    );
  });

  evidence.riskyIndicators?.forEach(indicator => {
    lines.push(
      `| \`classification:Risky\` | ${indicator.description} | +${Math.abs(
        indicator.riskScore,
      )} | ${indicator.evidenceConfidence.toFixed(2)} | ${indicator.contextWeighting.toFixed(
        2,
      )} | ${indicator.temporalRelevance.toFixed(2)} |`,
    );
  });

  lines.push('');
  lines.push('**Composite Risk Score [-1,1]:**');
  lines.push('');
  lines.push(
    ` - Aggregate Benign Indicators Score (ABIS) = Σ(Benign Indicator: Score × Confidence × Context × Temporal) = \`${evidence.aggregateBenignIndicatorsScore.toFixed(
      2,
    )}\``,
  );
  lines.push(
    ` - Aggregate Risk Indicators Score (ARIS) = Σ(Risk Indicator: Score × Confidence × Context × Temporal) = \`${evidence.aggregateRiskyIndicatorsScore.toFixed(
      2,
    )}\``,
  );
  lines.push(
    ` - Composite Risk Score (CRS) = (ARIS + ABIS) / (ARIS - ABIS) = \`${evidence.normalizedTotalRiskScore.toFixed(
      2,
    )}\``,
  );
  lines.push(
    ` - Classification: Benign ≤ \`${evidence.benignThreshold.toFixed(
      2,
    )}\`, Inconclusive \`${evidence.benignThreshold.toFixed(
      2,
    )}\` to \`${evidence.riskyThreshold.toFixed(2)}\`, Risky ≥ \`${evidence.riskyThreshold.toFixed(
      2,
    )}\` = \`classification:${evidence.classification}\``,
  );

  return lines.join('\n');
};

// Injects Risk Score Evidence from API data as a collapsible block after Risk Classification
export const injectRiskScoreEvidenceBlock = (
  text: string,
  evidence: RiskScoreEvidence | null | undefined,
): string => {
  const evidenceContent = formatRiskScoreEvidence(evidence);

  // If no evidence, return text as-is
  if (!evidenceContent) {
    return text;
  }

  const lines = text.split('\n');
  const riskClassificationIndex = lines.findIndex(
    line => line === '## Risk Classification' || line.startsWith('## Risk Classification:'),
  );

  if (riskClassificationIndex === -1) {
    return text;
  }

  // Find the end of the one-sentence summary (first non-empty line after the header)
  let summaryEndIndex = riskClassificationIndex + 1;
  let foundSummary = false;

  for (let i = riskClassificationIndex + 1; i < lines.length; i += 1) {
    const line = lines[i].trim();

    if (!foundSummary) {
      if (line !== '') {
        foundSummary = true;
        summaryEndIndex = i;
      }
    } else if (line === '' || line.startsWith('#')) {
      break;
    }
  }

  // Wrap in collapsible block with tab indentation
  const indentedContent = evidenceContent
    .split('\n')
    .map(line => `\t${line}`)
    .join('\n');

  const collapsibleBlock = `\`\`\`riskscore\n${indentedContent}\n${RISK_SCORE_BLOCK_END}\n\`\`\``;

  const beforeInjection = lines.slice(0, summaryEndIndex + 1);
  const afterInjection = lines.slice(summaryEndIndex + 1);

  return [...beforeInjection, '', collapsibleBlock, ...afterInjection].join('\n');
};
